import React, { useState, useEffect } from "react";
import {
  useToast,
  useTheme,
  Text,
  Flex,
  Heading,
  FormControl,
} from "@chakra-ui/core";
import { useAuth } from "../utils/auth";
import AuthLayout from "../components/layouts/AuthLayout";
import useAuthErrors from "../components/hooks/useAuthErrors";
import InputWithLabel from "../components/inputs/InputWithLabel";
import PrimaryButton from "../components/buttons/PrimaryButton";
import Link from "../components/Link";
import Padlock from "../components/icons/Padlock";
import UserPlus from "../components/icons/UserPlus";
import TopToast from "../components/toasts/TopToast";

const SUCCESS_MESSAGE_TIMEOUT = 9000;

const ResetPassword = () => {
  const auth = useAuth();
  const toast = useToast();
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [hasSentEmail, setHasSentEmail] = useState(false);
  const { handleGoogleError, reactHookForm } = useAuthErrors();
  const { handleSubmit, register, errors } = reactHookForm;
  const emailErrorText = errors.email && errors.email.message;

  useEffect(() => {
    let timeoutId;
    if (hasSentEmail) {
      timeoutId = setTimeout(() => {
        setHasSentEmail(false);
      }, SUCCESS_MESSAGE_TIMEOUT);
    }
    return () => {
      clearTimeout(timeoutId);
    };
  });

  const sendPasswordResetEmail = (email) => {
    if (isLoading) return false;
    setIsLoading(true);
    auth
      .sendPasswordResetEmail(email)
      .then(() => {
        setIsLoading(false);
        setHasSentEmail(true);
        toast({
          duration: SUCCESS_MESSAGE_TIMEOUT,
          position: "top",
          render: function Toast(props) {
            return (
              <TopToast
                {...props}
                title="An email has been sent"
                description="Please check your email for instructions on how to reset your password."
              />
            );
          },
        });
      })
      .catch((error) => {
        setIsLoading(false);
        handleGoogleError(error);
      });
  };

  return (
    <AuthLayout
      backgroundImageUrl="/images/pass_reset.png"
      footerContent={
        <>
          <Text color="greyscale7" mb={2}>
            Want to do something else?
          </Text>
          <Flex direction="row" justify="flex-end">
            <Link href="/signin" mb={2} mr={6}>
              <Flex direction="row">
                <Padlock color={theme.colors.orchidBlue} height={20} />
                <Text color="orchidBlue" ml={2}>
                  Login
                </Text>
              </Flex>
            </Link>
            <Link href="/signup" mb={2}>
              <Flex direction="row">
                <UserPlus color={theme.colors.orchidBlue} height={20} />
                <Text color="orchidBlue" ml={2}>
                  Create an account
                </Text>
              </Flex>
            </Link>
          </Flex>
        </>
      }
    >
      <Flex direction="column" justify="center">
        <Heading
          as="h3"
          fontSize="36px"
          fontWeight="normal"
          letterSpacing="3px"
          color="orchidBlue"
          mb={6}
        >
          Reset Your Password
        </Heading>
        <Text color="orchidGrey600" mb={6} fontSize="18px">
          Check your inbox for a link from us to reset your password. It may
          take up to 5 minutes to show up for you.
        </Text>
        <Flex
          as="form"
          direction="column"
          onSubmit={handleSubmit((data) => sendPasswordResetEmail(data.email))}
        >
          <FormControl mb={6} isInvalid={emailErrorText}>
            <InputWithLabel
              label="Email:"
              aria-label="Email Address"
              id="email"
              name="email"
              ref={register({
                required: "Please enter your email.",
              })}
              placeholder="name@site.com"
            />
            <Flex justifyContent="flex-end">
              <Text fontSize="12px" color="red">
                {emailErrorText}
              </Text>
            </Flex>
          </FormControl>
          <PrimaryButton disabled={isLoading || hasSentEmail}>
            {hasSentEmail ? "Successfully sent" : "Send the reset link"}
          </PrimaryButton>
        </Flex>
      </Flex>
    </AuthLayout>
  );
};

export default ResetPassword;
